import { useState, useEffect, useRef } from 'react';
import { 
  FiInfo, 
  FiX, 
  FiDownload,
  FiRefreshCw
} from 'react-icons/fi';
import { FaYoutube, FaMusic, FaVideo } from 'react-icons/fa';

import * as youtubeService from '../../../services/youtubeService';

const YoutubeDownloader = () => {
  // State for URL input
  const [url, setUrl] = useState('');
  
  // State for video information
  const [videoInfo, setVideoInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  
  // State for download options
  const [selectedFormat, setSelectedFormat] = useState('');
  const [quality, setQuality] = useState('best');
  const [audioOnly, setAudioOnly] = useState(false);
  
  // State for download status
  const [downloading, setDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);
  
  // State for errors
  const [error, setError] = useState(null);
  
  // Reference to status polling controller
  const statusPollingRef = useRef(null);
  
  // Clean up status polling on unmount
  useEffect(() => {
    return () => {
      if (statusPollingRef.current) {
        statusPollingRef.current.stop();
      }
    };
  }, []);
  
  // Reset states when changing URL
  useEffect(() => {
    if (videoInfo && url && url !== videoInfo.url) {
      handleReset();
    }
  }, [url, videoInfo]);
  
  // Handler for fetching video information
  const handleGetInfo = async () => {
    if (!url) {
      setError('Please enter a YouTube URL');
      return;
    }
    
    setError(null);
    setLoadingInfo(true);
    
    try {
      const info = await youtubeService.getVideoInfo(url);
      setVideoInfo({ ...info, url }); // Store the URL with the info for reference
      setLoadingInfo(false);
      
      // Auto-select best format
      if (info.formats && info.formats.length > 0) {
        // Find best quality video format
        const bestFormat = info.formats.reduce((prev, current) => {
          const prevSize = prev.filesize || 0;
          const currentSize = current.filesize || 0;
          
          // Prefer larger files (likely better quality)
          return currentSize > prevSize ? current : prev;
        });
        
        setSelectedFormat(bestFormat.format_id);
      }
    } catch (err) {
      setLoadingInfo(false);
      setError(`Error fetching video info: ${err.message}`);
      console.error('Error fetching video info:', err);
    }
  };
  
  // Handler for starting download
  const handleDownload = async () => {
    setError(null);
    setDownloading(true);
    setDownloadStatus(null);
    
    try {
      const options = {
        format_id: selectedFormat || undefined,
        quality,
        audio_only: audioOnly,
      };
      
      const downloadTask = await youtubeService.downloadVideo(url, options);
      
      // Start polling for status updates
      if (statusPollingRef.current) {
        statusPollingRef.current.stop();
      }
      
      statusPollingRef.current = youtubeService.pollTaskStatus(
        downloadTask.task_id,
        (status) => {
          setDownloadStatus(status);
          
          if (status.status === 'completed' || status.status === 'failed') {
            setDownloading(false);
          }
          
          if (status.status === 'failed' && status.error) {
            setError(`Download failed: ${status.error}`);
          }
        }
      );
    } catch (err) {
      setDownloading(false);
      setError(`Error starting download: ${err.message}`);
      console.error('Error starting download:', err);
    }
  };
  
  // Handler for resetting the form
  const handleReset = () => {
    setVideoInfo(null);
    setSelectedFormat('');
    setDownloadStatus(null);
    setError(null);
    
    if (statusPollingRef.current) {
      statusPollingRef.current.stop();
      statusPollingRef.current = null;
    }
  };
  
  // Render download progress
  const renderProgress = () => {
    if (!downloadStatus) return null;
    
    const { status, progress } = downloadStatus;
    
    let statusText = 'Preparing...';
    if (status === 'processing') statusText = 'Processing...';
    if (status === 'downloading') statusText = `Downloading: ${Math.round(progress)}%`;
    if (status === 'completed') statusText = 'Download completed!';
    if (status === 'failed') statusText = 'Download failed';
    
    return (
      <div className="mt-4">
        <p className="text-lg font-medium mb-2 text-white">
          {statusText}
        </p>
        
        {status !== 'completed' && status !== 'failed' && (
          <div className="w-full h-2 bg-[#2a2a2a] rounded-full mb-4">
            <div 
              className="h-full bg-[#ff1b6d] rounded-full transition-all duration-300"
              style={{ width: `${progress || 0}%` }}
            ></div>
          </div>
        )}
        
        {status === 'completed' && downloadStatus.result && (
          <div className="bg-[#1e3a3a] border-l-4 border-[#0d9488] p-4 rounded mb-4">
            <p className="text-sm text-white">
              File: {downloadStatus.result.filename}
              <br />
              Size: {youtubeService.formatFileSize(downloadStatus.result.size)}
              <br />
              Duration: {youtubeService.formatDuration(downloadStatus.result.duration)}
            </p>
          </div>
        )}
      </div>
    );
  };
  
  // Quality options for the dropdown
  const qualityOptions = [
    { value: 'best', label: 'Best Quality' },
    { value: 'high', label: 'High (1080p)' },
    { value: 'medium', label: 'Medium (720p)' },
    { value: 'low', label: 'Low (480p)' },
    { value: 'worst', label: 'Lowest Quality' }
  ];
  
  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-2 text-center text-white">
        YouTube Downloader
      </h1>
      
      <p className="text-lg text-center text-gray-300 mb-8">
        Download videos from YouTube by entering the URL below.
      </p>
      
      {/* URL Input Section */}
      <div className="bg-[#1e1e1e] rounded-lg p-6 mb-6 border border-[#2a2a2a]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          <div className="md:col-span-7">
            <div className="relative">
              <input
                type="text"
                className="w-full bg-[#121212] border border-[#2a2a2a] rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-[#ff1b6d] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="https://www.youtube.com/watch?v=..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={loadingInfo || downloading}
              />
              <FaYoutube className="absolute right-3 top-3 text-[#ff1b6d]" size={20} />
            </div>
          </div>
          
          <div className="md:col-span-5">
            <div className="flex gap-2">
              <button
                onClick={handleGetInfo}
                disabled={!url || loadingInfo || downloading}
                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium w-full transition-colors ${
                  !url || loadingInfo || downloading
                    ? "bg-[#2a2a2a] text-gray-500 cursor-not-allowed"
                    : "bg-[#ff1b6d] text-white hover:bg-[#ff1b6d]/90"
                }`}
              >
                {loadingInfo ? (
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  <>
                    <FiInfo className="w-5 h-5" />
                    <span>Get Info</span>
                  </>
                )}
              </button>
              
              <button
                onClick={() => {
                  setUrl('');
                  handleReset();
                }}
                disabled={!url || loadingInfo || downloading}
                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors border ${
                  !url || loadingInfo || downloading
                    ? "border-[#2a2a2a] text-gray-500 cursor-not-allowed"
                    : "border-[#ff1b6d] text-[#ff1b6d] hover:bg-[#ff1b6d]/10"
                }`}
              >
                <FiX className="w-5 h-5" />
                <span>Clear</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Error Alert */}
      {error && (
        <div className="bg-[#3a1e1e] border-l-4 border-[#f43f5e] p-4 rounded mb-6">
          <p className="text-white">{error}</p>
        </div>
      )}
      
      {/* Video Information */}
      {videoInfo && (
        <div className="bg-[#1e1e1e] rounded-lg mb-6 overflow-hidden border border-[#2a2a2a]">
          <div className="grid grid-cols-1 md:grid-cols-12">
            {videoInfo.thumbnail && (
              <div className="md:col-span-4">
                <img 
                  src={videoInfo.thumbnail} 
                  alt={videoInfo.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            <div className={`p-6 ${videoInfo.thumbnail ? 'md:col-span-8' : 'md:col-span-12'}`}>
              <h2 className="text-xl font-bold text-white mb-2">
                {videoInfo.title}
              </h2>
              
              <p className="text-gray-400 mb-4">
                Duration: {youtubeService.formatDuration(videoInfo.duration)}
              </p>
              
              {/* Download Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-300 mb-2">Format</label>
                  <select
                    value={selectedFormat}
                    onChange={(e) => setSelectedFormat(e.target.value)}
                    disabled={downloading}
                    className="w-full bg-[#121212] border border-[#2a2a2a] rounded-lg py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-[#ff1b6d] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {videoInfo.formats && videoInfo.formats.map((format) => (
                      <option key={format.format_id} value={format.format_id}>
                        {format.resolution} - {format.extension} 
                        {format.filesize ? ` (${youtubeService.formatFileSize(format.filesize)})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-gray-300 mb-2">Quality</label>
                  <select
                    value={quality}
                    onChange={(e) => setQuality(e.target.value)}
                    disabled={audioOnly || downloading}
                    className="w-full bg-[#121212] border border-[#2a2a2a] rounded-lg py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-[#ff1b6d] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {qualityOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex items-center mb-4">
                <label className="inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer"
                    checked={audioOnly}
                    onChange={(e) => setAudioOnly(e.target.checked)}
                    disabled={downloading}
                  />
                  <div className="relative w-11 h-6 bg-[#2a2a2a] rounded-full peer peer-checked:bg-[#ff1b6d] peer-disabled:opacity-50 transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                  <span className="ml-3 text-white">Audio Only (MP3)</span>
                </label>
              </div>
              
              <button
                onClick={handleDownload}
                disabled={downloading || !videoInfo}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                  downloading || !videoInfo
                    ? "bg-[#2a2a2a] text-gray-500 cursor-not-allowed"
                    : audioOnly 
                      ? "bg-[#ff1b6d] text-white hover:bg-[#ff1b6d]/90" 
                      : "bg-[#ff1b6d] text-white hover:bg-[#ff1b6d]/90"
                }`}
              >
                {downloading ? (
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  <>
                    {audioOnly ? (
                      <FaMusic className="w-5 h-5" />
                    ) : (
                      <FaVideo className="w-5 h-5" />
                    )}
                    <span>{audioOnly ? 'Download Audio' : 'Download Video'}</span>
                  </>
                )}
              </button>
              
              {/* Download Progress */}
              {renderProgress()}
            </div>
          </div>
        </div>
      )}
      
      {/* Instructions */}
      {!videoInfo && !loadingInfo && (
        <div className="bg-[#1e1e1e] rounded-lg p-6 border border-[#2a2a2a]">
          <h2 className="text-xl font-bold text-white mb-4">
            How to use:
          </h2>
          <ol className="list-decimal pl-5 text-gray-300 space-y-2">
            <li>Paste a YouTube video URL in the input field above</li>
            <li>Click "Get Info" to fetch video details</li>
            <li>Select your preferred format and quality</li>
            <li>Click "Download Video" to start downloading</li>
          </ol>
        </div>
      )}
    </div>
  );
};

export default YoutubeDownloader;
